import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  Button,
  Stack,
  Divider,
} from '@mui/material';
import {
  Update as UpdateIcon,
  CheckCircle as CheckIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  Schedule as ScheduleIcon,
} from '@mui/icons-material';
import { Device } from '../../types/api';
import InfoRow from '../common/InfoRow';

interface DeviceVersionCardProps {
  device: Device;
  latestVersion?: string;
  onUpdateClick?: () => void;
}

const DeviceVersionCard: React.FC<DeviceVersionCardProps> = ({
  device,
  latestVersion, // Removed hardcoded default - should come from API
  onUpdateClick,
}) => {
  const currentVersion = device.clientVersion || 'Unknown';

  // Determine if update is available
  const isUpdateAvailable = latestVersion &&
                           currentVersion !== 'Unknown' &&
                           currentVersion !== latestVersion;

  // Version status
  const getVersionStatus = () => {
    if (currentVersion === 'Unknown') {
      return { color: 'warning' as const, icon: <WarningIcon />, label: 'Version Unknown' };
    }
    if (!latestVersion) {
      return { color: 'success' as const, icon: <CheckIcon />, label: 'Up to Date' };
    }
    if (currentVersion === latestVersion) {
      return { color: 'success' as const, icon: <CheckIcon />, label: 'Up to Date' };
    }
    return { color: 'info' as const, icon: <InfoIcon />, label: 'Update Available' };
  };

  const versionStatus = getVersionStatus();

  // Calculate how many versions behind
  const versionsBehind = () => {
    if (!latestVersion || currentVersion === 'Unknown' || currentVersion === latestVersion) {
      return 0;
    }
    // This is simplified - in reality, you'd check against all available versions
    const current = currentVersion.split('.').map(Number);
    const latest = latestVersion.split('.').map(Number);

    if (current[0] < latest[0]) return 'major';
    if (current[1] < latest[1]) return 'minor';
    if (current[2] < latest[2]) return 'patch';
    return 'none';
  };

  const updateType = versionsBehind();

  return (
    <Card>
      <CardContent>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 2
          }}>
          <Typography variant="h6" component="h2">
            Version Information
          </Typography>
          <Chip
            icon={versionStatus.icon}
            label={versionStatus.label}
            color={versionStatus.color}
            size="small"
          />
        </Box>

        {/*
          This card answers one question — is an update due? — so it holds only
          the current/latest comparison. Operating System and Firmware Version
          used to be repeated here as well, in a second, larger type treatment
          than the Device Information card beside it; they now live only there.
        */}
        <Stack spacing={2}>
          <InfoRow
            label="Installed"
            value={currentVersion}
            action={
              isUpdateAvailable && (
                <Chip
                  label={`${updateType} update`}
                  size="small"
                  color={updateType === 'major' ? 'warning' : 'info'}
                  variant="outlined"
                />
              )
            }
          />

          {isUpdateAvailable && <InfoRow label="Latest Available" value={latestVersion} />}

          <Divider />

          {/* Update Information */}
          {isUpdateAvailable && (
            <>
              <Box>
                <Typography variant="body2" gutterBottom sx={{
                  color: 'text.secondary'
                }}>
                  Update Details
                </Typography>
                <Stack spacing={1}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1
                    }}>
                    <InfoIcon fontSize="small" color="info" />
                    <Typography variant="body2">
                      New version {latestVersion} is available
                    </Typography>
                  </Box>
                  {updateType === 'major' && (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1
                      }}>
                      <WarningIcon fontSize="small" color="warning" />
                      <Typography variant="body2" sx={{
                        color: 'warning.main'
                      }}>
                        Major version update - may include breaking changes
                      </Typography>
                    </Box>
                  )}
                </Stack>
              </Box>

              {/* Update Button */}
              <Button
                variant="contained"
                color="primary"
                startIcon={<UpdateIcon />}
                onClick={onUpdateClick}
                fullWidth
              >
                Update to {latestVersion}
              </Button>
            </>
          )}

          {/* Up to date message */}
          {!isUpdateAvailable && currentVersion !== 'Unknown' && (
            <Box
              sx={{
                p: 2,
                bgcolor: 'success.light',
                borderRadius: 1,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <CheckIcon color="success" />
              <Typography variant="body2">
                Device is running the latest version
              </Typography>
            </Box>
          )}

          {/* Unknown version warning */}
          {currentVersion === 'Unknown' && (
            <Box
              sx={{
                p: 2,
                bgcolor: 'warning.light',
                borderRadius: 1,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <WarningIcon color="warning" />
              <Box>
                <Typography variant="body2" sx={{
                  fontWeight: 'bold'
                }}>
                  Version information unavailable
                </Typography>
                <Typography variant="caption">
                  The device hasn't reported its version yet. This will update on the next heartbeat.
                </Typography>
              </Box>
            </Box>
          )}

          {/* Last Updated */}
          {device.lastSeen && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                mt: 1
              }}>
              <ScheduleIcon fontSize="small" color="action" />
              <Typography variant="caption" sx={{
                color: 'text.secondary'
              }}>
                Last updated: {new Date(device.lastSeen).toLocaleString()}
              </Typography>
            </Box>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
};

export default DeviceVersionCard;
